import { useEffect, useRef, useState } from "react";
import { initializeLogger } from "../../core/config/config.initializer";
import { Logger, LogLevel } from "@core/logger";
import { CommonState } from "../../core/interface/common-state";
import { useParams } from "react-router-dom";
import { ChartType } from "../../core/interface/chart-types";
import { DashboardDetail, Templatejson, WidgetContent } from "../../core/interface/dashboard-detail";
import { getConversationId, setConversationId } from "../../core/services/user.service";
import { CommonResponse } from "../../core/interface/common-response";
import { axiosGet, axiosPost } from "../../core/services/http-call.service";
import { WidgetData } from "../../core/interface/widget-data";
import { extractYearAndValue, extractYearAndValueForChart, transformChartOption } from "../../core/util/util-functions";
import { ChatData, SqlDataResp } from "../../core/interface/chat-data";
import { subscribeEvent } from "@core/pubsub-nanoemitter";
import { FilterKeyValue, FilterObject } from "../../core/interface/filter-object";
import { GeoDataObject } from "../../core/interface/geodata-object";
import { ActualData, PredictionReq } from "../../core/interface/prediction-req";
import _ from 'lodash';

export default function useBulletinLogic() {

  let logger: Logger = initializeLogger();
  const [stateObject, setStateObject] = useState<CommonState>({
    showErrorModal: false, callInProgress: false, statusMessage: ''
  });
  const [showFilterCard, setShowFilterCard] = useState<boolean>(false);
  const [showSaveDashboardModal, setShowSaveDashboardModal] = useState<boolean>(false);
  const [predictData, setPredictData] = useState<any>({});

  const layoutEvent = 'chat.message:Layout';
  const dataEvent = 'chat.message:Data';

  const tempBulletinRef = useRef<DashboardDetail | null>(null);

  const bulletinId = useParams<{ id: string }>();
  const [bulletinDetail, setBulletinDetail] = useState<DashboardDetail | null>(null);
  const [isChatCollapsed, setIsChatCollapsed] = useState(false);
  const [chatData, setChatData] = useState<ChatData>();
  const [filterData, setFilterData] = useState<FilterObject[]>([])
  const [filteredData, setFilteredData] = useState<ChatData>();

  useEffect(() => {
    setConversationId('');
    const loadBulletinDetail = async () => {
      await getBulletinDetail();
    };

    if (bulletinId.id != '0') {
      logger.log(LogLevel.DEBUG, `Loading layout details for dashboard: ${bulletinId.id}`)
      loadBulletinDetail();
    }
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeEvent(layoutEvent, (data) => {
      logger.log(LogLevel.INFO, 'Received Layout data.', data);
      let tempLayoutStorage: DashboardDetail;
      if (bulletinDetail == null) {
        setStateObject({ ...stateObject, callInProgress: true });
        tempLayoutStorage = {
          id: 0,
          title: '',
          conversation_id: getConversationId() || '',
          description: '',
          template_json: data.recomendedLayout,
          filters: [],
          tags: []
        }
        setBulletinDetail(tempLayoutStorage);

      } else {
        tempLayoutStorage = {
          id: bulletinDetail?.id || 0,
          title: bulletinDetail?.title || '',
          conversation_id: bulletinDetail?.conversation_id || '',
          description: bulletinDetail?.description || '',
          template_json: data.recomendedLayout,
          filters: bulletinDetail?.filters || [],
          tags: bulletinDetail?.tags || []
        }
      }
      tempBulletinRef.current = tempLayoutStorage;
      setStateObject({ ...stateObject, callInProgress: false });
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeEvent('widget.added:NEW', (data) => {
      logger.log(LogLevel.INFO, 'Received event to add new widget.', data);
      setStateObject({ ...stateObject, callInProgress: true });
      const newWidgetId = Date.now().toString();
      let newLayout: Templatejson = {
        i: newWidgetId,
        h: 14,
        w: 3,
        x: 0,
        y: Infinity,
        content: {
          "query": "",
          "widgetId": newWidgetId,
          "chartType": "CHART",
          "semanticSql": "",
          "widgetTitle": "",
          "downstreamSql": "",
          "widgetDescription": "",
          "chartOptionsObject": data.chartOptionsData
        }
      }

      bulletinDetail!.template_json.push(newLayout);
      setStateObject({ ...stateObject, callInProgress: false });
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeEvent(dataEvent, (data) => {
      logger.log(LogLevel.INFO, 'Received Chart data.', data);
      const temp = tempBulletinRef.current;
      if (!temp) {
        logger.log(LogLevel.ERROR, 'Temp layout is not available!');
        return;
      }

      temp.conversation_id = getConversationId() || '';
      let widgetData: ChatData = data;
      if (widgetData.response == "" && widgetData.error == "") {
        // setStateObject({ ...stateObject, callInProgress: true });
        logger.log(LogLevel.DEBUG, 'Moving the temp layout to main layout', temp)
        setBulletinDetail(temp);
        setChatData(widgetData);
        setFilteredData(JSON.parse(JSON.stringify(widgetData)));
        // temp.template_json.forEach(layout => {
        //   emitEvent(`widget.added:${layout.content.widgetId}`, createWidgetDataFromNlqResult(widgetData, layout))
        // })
        populateFilters(widgetData)
        setStateObject({ ...stateObject, callInProgress: false });
      }
    });
    return () => unsubscribe();
  }, []);

  async function getBulletinDetail() {
    setStateObject({ ...stateObject, callInProgress: true });
    let resp: CommonResponse = await axiosGet(import.meta.env.VITE_API_HOST + `/dashboard/${bulletinId.id}`);

    if (resp.code == 'SUCCESS' && resp.data != null) {
      const tempTemplateJson: Templatejson[] = [];
      resp.data.template_json.forEach((template: { content: { chartOptionsObject: any[]; }; }) => {
        const clonedTemplate = JSON.parse(JSON.stringify(template));
        clonedTemplate.content.chartOptionsObject = template.content.chartOptionsObject.map(
          (opt: any) => JSON.parse(JSON.stringify(opt))
        );
        tempTemplateJson.push(clonedTemplate);
        console.log(tempTemplateJson)
      })
      const bulletinDetail: DashboardDetail = {
        id: resp.data.id,
        title: resp.data.title,
        description: resp.data.description,
        conversation_id: resp.data.conversation_id,
        template_json: tempTemplateJson,
        filters: [],
        tags: []
      };
      // let bulletinDetail: DashboardDetail = structuredClone(resp.data);
      console.log(bulletinDetail)
      setBulletinDetail(bulletinDetail);
      setConversationId(bulletinDetail.conversation_id);
      await publishWidgetData(bulletinDetail);
      setStateObject({ ...stateObject, callInProgress: false });
    } else {
      setStateObject({ ...stateObject, callInProgress: false, showErrorModal: true, statusMessage: 'Unable to fetch valid dashboard details.' })
    }
  }

  function populateFilters(chatData: ChatData) {
    let measures = chatData?.columnsMetadata?.filter(x => x.isMeasure == false);
    let filters: FilterObject[] = [];
    measures.forEach(measure => {
      let filterKeys: FilterKeyValue[] = [];
      chatData.data.forEach(data => {
        if (!filterKeys.some(obj => '' + measure.colName in obj))
          //@ts-ignore
          filterKeys.push({ key: data[measure.colName], value: data[measure.colName] })
      })
      if (filterKeys.length > 0)
        filters.push({
          title: measure.title,
          colName: measure.colName,
          options: filterKeys
        })
    })
    setFilterData(filters);
  }

  function handleFilterSelect(e: any, keyname: string) {
    if (e.target.value != 'Select filter...') {
      setStateObject({ ...stateObject, callInProgress: true });
      const originalData = JSON.parse(JSON.stringify(chatData?.data || []));
      if (originalData.some((obj: any) => keyname in obj)) {
        filteredData!.data = originalData.filter((x: { [x: string]: string; }) => x[keyname] === e.target.value);
      }
      setStateObject({ ...stateObject, callInProgress: false });
    }
  }

  function resetChatData() {
    setStateObject({ ...stateObject, callInProgress: true });
    filteredData!.data = chatData?.data || [];
    setStateObject({ ...stateObject, callInProgress: false });
  }

  function createWidgetDataFromNlqResult(chatData: ChatData, templateData: Templatejson) {
    let widgetContent: WidgetContent = templateData.content;
    let widgetData: WidgetData = {
      widgetId: widgetContent.widgetId,
      widgetTitle: widgetContent.widgetTitle,
      widgetDescription: widgetContent.widgetDescription,
      widgetType: getWidgetType(widgetContent.chartType, chatData),
      chartOptionsData: []
    };

    if (widgetData.widgetType == "BAR_CHART" || widgetData.widgetType == "COLUMN_CHART" || widgetData.widgetType == "LINE_CHART"
      || widgetData.widgetType == "DONUT_CHART" || widgetData.widgetType == "HISTOGRAM_CHART" || widgetData.widgetType == "PIE_CHART"
      || widgetData.widgetType == "SCATTER_PLOT" || widgetData.widgetType == "STEP_LINE_CHART" || widgetData.widgetType == "AREA_CHART"
    ) {
      chatData.advises.charts.forEach((chart: any) => {
        let optionData = chart;
        let transformedData = transformChartOption(optionData, chatData.columnsMetadata, filteredData?.data);
        widgetData.chartOptionsData!.push(transformedData);
      })
      widgetData.widgetType = "CHART"
    } else if (widgetData.widgetType == "DATA_TABLE") {
      widgetData.columnMetaData = chatData?.columnsMetadata;
      widgetData.tableData = chatData?.data;
      widgetData.widgetType = "TABLE";
    } else if (widgetData.widgetType == "PREDICTION_CHART") {
      let tempOptionsData: Object[] = []
      tempOptionsData.push(predictData)
      widgetData.chartOptionsData = tempOptionsData;
      if (widgetData.chartOptionsData.length == 0) {
        widgetData.chartOptionsData = [{
          chartType: 'line_chart',
          xAxis: [{
            type: 'category'
          }],
          yAxis: [{
            type: 'value'
          }],
          series: []
        }]
      }
      widgetData.widgetType = "PREDICTION_CHART"
      console.log(widgetData)
    } else if (widgetData.widgetType == "GEO") {
      let geoData: GeoDataObject[] = [];
      let addressField = chatData.columnsMetadata.find(x => x.colName.toLowerCase() == 'address')
      if (addressField != undefined) {
        let measureName: string = chatData.columnsMetadata.find(x => x.isMeasure == true)?.colName || '';
        const key = Object.keys(chatData?.data[0]).find(k => k.toLowerCase() === measureName.toLowerCase());
        chatData?.data?.forEach((data: Record<string, any>) => {
          geoData.push({
            name: data.address,
            value: key ? data[key] : null
          })
        })
      }

      widgetData.chartOptionsData = [{ series: { data: geoData } }]
      widgetData.widgetType = "GEO"
    } else if (widgetData.widgetType == "AGGREGATE") {
      widgetData.tableData = chatData.data;
      widgetData.columnMetaData = chatData.columnsMetadata;
      widgetData.chartOptionsData = []
      widgetData.widgetType = "AGGREGATE"
    }

    updateBulletinDetails(widgetData, chatData?.advises?.charts);
    // console.log('chartType:', widgetData.widgetType, widgetData)
    return widgetData
  }

  function transformToSeries(actualData: any, forecastData: any) {

    if (forecastData.length > 0) {
      actualData.push(forecastData[0]);
    }

    let yearValuesActual = extractYearAndValueForChart(actualData);
    let yearValuesForecast = extractYearAndValueForChart(forecastData);

    let paddedForecastValues = new Array(yearValuesActual.values.length - 1).fill(null).concat(yearValuesForecast.values);

    let seriesData = {
      chartType: 'line_chart',
      legend: {
        data: ['Actual', 'Forecast']
      },
      xAxis: [{
        type: 'category',
        data: [...yearValuesActual.years, ...yearValuesForecast.years],
        name: 'Years'
      }],
      yAxis: [{
        type: 'value',
        name: 'Value'
      }],
      series: [
        {
          name: 'Actual',
          type: 'line',
          data: yearValuesActual.values
        },
        {
          name: 'Forecast',
          type: 'line',
          lineStyle: {
            type: 'dashed'
          },
          data: paddedForecastValues
        }
      ]
    }
    return seriesData;
  }


  function getWidgetType(templateChartType: string, chatData: ChatData) {
    let widgetType: ChartType = "DNS";
    switch (templateChartType) {
      case "CHART":
        widgetType = (chatData?.advises?.charts?.length > 0 ? chatData?.advises?.charts[0].type.toUpperCase() : "DNS") as ChartType
        break;
      case "AGGREGATE":
        widgetType = "AGGREGATE";
        break;
      case "PREDICTION_CHART":
        widgetType = "PREDICTION_CHART"
        break;
      case "TABLE":
        widgetType = "DATA_TABLE";
        break;
      case "GEO":
        widgetType = "GEO";
        break;
      default:
        widgetType = "DNS"
    }

    return widgetType;
  }

  async function publishWidgetData(bulletinDtl: DashboardDetail) {
    // setStateObject({ ...stateObject, callInProgress: true });
    let templateData: Templatejson[] = bulletinDtl?.template_json || [];
    let widgetContent: WidgetContent = templateData[0].content;
    let sqlReq = {
      query: widgetContent.query,
      semanticSql: widgetContent.semanticSql,
      downstreamSql: widgetContent.downstreamSql
    }
    let resp: CommonResponse = await axiosPost(import.meta.env.VITE_API_HOST + '/nlq/sql', sqlReq);
    let sqlData: SqlDataResp = resp.data;
    let dimensionData = sqlData.columnsMetadata.find(x => x.datatype == 'datetime');
    let measureData = sqlData.columnsMetadata.find(x => x.isMeasure == true);
    let predicted: any = {
      chartType: 'line_chart',
      xAxis: [{
        type: 'category'
      }],
      yAxis: [{
        type: 'value'
      }],
      series: []
    };
    if (dimensionData != undefined && measureData != undefined) {
      let actualData: ActualData[] = [];
      const key = Object.keys(sqlData?.data[0]).find(k => k.toLowerCase() === measureData.colName.toLowerCase());
      sqlData.data.forEach((row: Record<string, any>) => {
        actualData.push({
          period_type: "date",
          time_period_data: row[dimensionData.colName],
          value: key ? '' + row[key] : '0'
        })
      })
      let predReq: PredictionReq = {
        data: actualData,
        future_period_type: "year",
        future_period: 10
      }

      let predResp = await axiosPost(import.meta.env.VITE_API_HOST + '/agents/api/v1/models/forecast', predReq);
      predicted = transformToSeries(extractYearAndValue(actualData), predResp)
      setPredictData(predicted)
    }
    let chatDataTemp: ChatData = {
      advises: { charts: widgetContent.chartOptionsObject, groupedSummary: [], numericSummary: [] },
      data: resp.data.data,
      columnsMetadata: resp.data.columnsMetadata,
      response: "",
      semanticSQL: widgetContent.semanticSql,
      downstreamSQL: widgetContent.downstreamSql,
      query: widgetContent.query,
      error: "",
      columnDetails: resp.data.columnsMetadata,
      predictResp: predicted
    }
    setChatData(chatDataTemp);
    setFilteredData(JSON.parse(JSON.stringify(chatDataTemp)));
    populateFilters(chatDataTemp)
    // setStateObject({ ...stateObject, callInProgress: false });
  }

  async function getPredictionData(data: ChatData) {
    let dimensionData = data?.columnsMetadata?.find(x => x.datatype == 'datetime');
    let measureData = data?.columnsMetadata?.find(x => x.isMeasure == true);
    let predicted: any = {
      chartType: 'line_chart',
      xAxis: [{
        type: 'category'
      }],
      yAxis: [{
        type: 'value'
      }],
      series: []
    };
    if (dimensionData != undefined && measureData != undefined) {
      let actualData: ActualData[] = [];
      const key = Object.keys(data?.data[0]).find(k => k.toLowerCase() === measureData.colName.toLowerCase());
      data.data.forEach((row: Record<string, any>) => {
        actualData.push({
          period_type: "date",
          time_period_data: row[dimensionData.colName],
          value: key ? '' + row[key] : '0'
        })
      })
      let predReq: PredictionReq = {
        data: actualData,
        future_period_type: "year",
        future_period: 10
      }

      let predResp = await axiosPost(import.meta.env.VITE_API_HOST + '/agents/api/v1/models/forecast', predReq);
      predicted = transformToSeries(extractYearAndValue(actualData), predResp);
    }
    return predicted;
  }

  // function subscribeForRemoveWidget(widgetId: string) {
  //   let unsubscribeEvent = pubSub.subscribe(EVENT_ID_REMOVE_WIDGET, (data: any) => {
  //     logger.log(LogLevel.INFO, 'Remove widget.', data);
  //     removeWidget(widgetId)
  //   }, widgetId);
  //   return () => unsubscribeEvent();
  // }

  function addWidget() {
    // setStateObject({ ...stateObject, callInProgress: true });
    // const newWidgetId = Date.now().toString();
    // let optionData = chatDataStore?.advises.charts[0];
    // let transformedData = transformChartOption(optionData, "line")
    // let newLayout = {
    //   i: newWidgetId,
    //   h: 16,
    //   w: 4,
    //   x: 0,
    //   y: Infinity,
    //   content: {
    //     "query": "",
    //     "widgetId": newWidgetId,
    //     "chartType": "LINE_CHART" as ChartType,
    //     "semanticSql": "",
    //     "widgetTitle": "",
    //     "downstreamSql": "",
    //     "widgetDescription": "",
    //     "chartOptionsObject": transformedData
    //   }
    // }

    // bulletinDetail!.template_json.push(newLayout);
    // setStateObject({ ...stateObject, callInProgress: false });
  }

  // function removeWidget(widgetId: string) {
  //   setStateObject({ ...stateObject, callInProgress: true });
  //   bulletinDetail!.template_json = bulletinDetail!.template_json.filter(x => x.i !== widgetId);
  //   setStateObject({ ...stateObject, callInProgress: false });
  // }

  async function updateBulletinDetails(widgetData: WidgetData, chartAdvises: any) {
    let contentData: WidgetContent = {
      widgetId: widgetData.widgetId,
      widgetTitle: widgetData.widgetTitle,
      widgetDescription: widgetData.widgetDescription,
      query: chatData?.query || '',
      semanticSql: chatData?.semanticSQL || '',
      downstreamSql: chatData?.downstreamSQL || '',
      chartType: widgetData.widgetType,
      chartOptionsObject: chartAdvises
    };

    for (var i in bulletinDetail?.template_json) {
      if (bulletinDetail.template_json[+i].i == contentData.widgetId)
        bulletinDetail.template_json[+i].content = contentData;
    }
  }

  function saveDashboard() {
    // console.log(bulletinDetail)
  }

  function handleLayoutChanges(newLayout: any) {
    let currentLayout = bulletinDetail || null;
    let updatedLayout: Templatejson[] = [];
    newLayout.forEach((layoutEntry: { i: string; x: number; y: number; h: number; w: number; }) => {
      if (currentLayout != null) {
        let currentItem = currentLayout?.template_json.filter(x => x.i === layoutEntry.i)[0];
        let updatedLayoutProps = {
          i: currentItem!.i,
          x: layoutEntry.x,
          y: layoutEntry.y,
          h: layoutEntry.h,
          w: layoutEntry.w,
          content: currentItem!.content
        }
        updatedLayout.push(updatedLayoutProps);
      }
    });

    if (currentLayout != null)
      currentLayout.template_json = updatedLayout;
    setBulletinDetail(currentLayout);
  }

  function handleCloseModal() {
    setShowSaveDashboardModal(false);
    // setShowAddWidgetModal(false);
  };

  async function addSection(sectionType: "GEO" | "AGGREGATE" | "PREDICTION_CHART") {
    setStateObject({ ...stateObject, callInProgress: true });
    const newWidgetId = Date.now().toString();
    const layoutTemplate: Templatejson = {
      "h": 14,
      "i": newWidgetId,
      "w": 3,
      "x": 0,
      "y": Infinity,
      "content": {
        query: "",
        widgetId: newWidgetId,
        chartType: sectionType,
        semanticSql: "",
        widgetTitle: sectionType == "AGGREGATE" ? 'Aggregate view' : sectionType == "GEO" ? 'Geo chart' : sectionType == "PREDICTION_CHART" ? 'Prediction view' : '',
        downstreamSql: "",
        widgetDescription: "",
        chartOptionsObject: []
      }
    }

    if (sectionType === "PREDICTION_CHART" && !chatData?.predictResp) {
      const predictResp = await getPredictionData(chatData!);
      setPredictData(predictResp)
    }
    const updatedTemplateJson = [...(bulletinDetail?.template_json || []), layoutTemplate];
    setBulletinDetail(prev => ({ ...prev as DashboardDetail, template_json: updatedTemplateJson }));
    setStateObject({ ...stateObject, callInProgress: false });
  }

  return {
    stateObject,
    bulletinDetail,
    showFilterCard,
    setShowFilterCard,
    addWidget,
    saveDashboard,
    handleLayoutChanges,
    setShowSaveDashboardModal,
    showSaveDashboardModal,
    handleCloseModal,
    isChatCollapsed,
    setIsChatCollapsed,
    createWidgetDataFromNlqResult,
    chatData,
    bulletinId,
    filterData,
    addSection,
    handleFilterSelect,
    filteredData,
    resetChatData
  }
}